import _ from 'lodash';

export class CardBuilder {
  constructor(parent, bindToParent) {
    if (bindToParent) {
      this.card = parent;
    } else {
      this.card = _.merge({}, parent);
    }
    this.card.error = {
      hasError: false,
    };
    this.parent = parent;
  }

  setField(fieldName, value) {
    _.set(this.card, fieldName, value);
  }

  field(fieldName, setter, mapping) {
    const value = this.readValueFromParent(setter, mapping);
    this.setField(fieldName, value);
  }

  fields(fieldName, setter, mapping) {
    const value = this.readValueFromParent(setter, mapping);
    _.forEach(value, (val, key) => {
      this.setField(`${fieldName}.${key}`, val);
    });
  }

  contractingPartners(onSelectOrBinding, selected) {
    const binding = this.createBindingIfNecessary(onSelectOrBinding, selected);
    const contractingPartners = {
      onSelect: binding.action,
      selected: binding.value,
    };
    this.setField('contractingPartners', contractingPartners);
  }

  multipleContracts() {
    // dont show contracts section for paypal etc.
    const multipleContracts =
      _.size(this.card.contracts) > 1 &&
      this.card.partner.preferredContact !== 'online';
    this.setField('multipleContracts', multipleContracts);
  }

  removable(onRemoveOrBinding, removed) {
    const binding = this.createBindingIfNecessary(onRemoveOrBinding, removed);
    const removable = {
      onRemove: binding.action,
      removed: binding.value,
    };
    this.setField('removable', removable);
  }

  readonly(readonly) {
    this.setField('readonly', readonly);
  }

  status(name, bindingPath, iconMapping) {
    if (!this.card.states) {
      this.card.states = [];
    }
    let value = this.readValueFromParent(bindingPath);
    if (value && iconMapping && iconMapping[value]) {
      value = value.toString();
      this.card.states.push({
        name,
        icon: iconMapping[value],
        value,
      });
    }
  }

  onChange(callback) {
    if (angular.isFunction(callback)) {
      this.onChangeCallback = callback;
    }
  }

  actionWithCallback(name, callback, active, options) {
    callback = this.wrapAction(callback);
    this.action(name, callback, active, options);
  }

  actionWithBinding(name, toBinding, mapping, options) {
    const binding = this.createBindingIfNecessary(
      toBinding,
      undefined,
      mapping,
    );
    this.action(name, binding.action, binding.value, options);
  }

  action(name, action, active, options, disabled = false) {
    if (!this.card.actions) {
      this.card.actions = [];
    }
    this.card.actions.push({
      name,
      action,
      active,
      disabled,
      options,
    });
    action(active || false);
  }

  toStartValue(fromValue, mapping) {
    const invertedMapping = _.invert(mapping);
    return this.readFromMapping(fromValue, invertedMapping);
  }

  fromStartValue(toValue, mapping) {
    return this.readFromMapping(toValue, mapping);
  }

  readFromMapping(value, mapping) {
    // eslint-disable-next-line no-prototype-builtins
    if (mapping && mapping.hasOwnProperty(value)) {
      return mapping[value];
    }
    return value;
  }

  readValueFromParent(bindingPathOrSetter, mapping) {
    if (angular.isFunction(bindingPathOrSetter)) {
      const value = bindingPathOrSetter(this.parent);
      return this.fromStartValue(value, mapping);
    }
    const value = _.get(this.parent, bindingPathOrSetter);
    return this.fromStartValue(value, mapping);
  }

  createBindingIfNecessary(callbackOrBinding, value, mapping) {
    let action = angular.noop;

    if (angular.isUndefined(value)) {
      value = this.readValueFromParent(callbackOrBinding, mapping);
    }

    if (angular.isString(callbackOrBinding)) {
      const callback = this.oneWayBinding(callbackOrBinding, mapping);
      action = this.wrapAction(callback);
    }
    if (angular.isFunction(callbackOrBinding)) {
      action = this.wrapAction(callbackOrBinding);
    }
    return { action, value };
  }

  oneWayBinding(toPath, mapping) {
    if (!toPath) {
      return angular.noop;
    }

    return (card, parent, value) => {
      let returnValue = this.toStartValue(value, mapping);
      this.setValue(parent, toPath, returnValue, card);

      if (
        card.error &&
        card.error.checkOn &&
        card.error.checkOn.indexOf(toPath) > -1
      ) {
        card.error.check();
      }
    };
  }

  twoWayBinding(toPath, fromPath) {
    if (!(toPath && fromPath)) {
      return {
        action: angular.noop,
        reset: angular.noop,
      };
    }

    const action = (card, parent) => {
      // eslint-disable-next-line angular/timeout-service
      setTimeout(() => {
        let value = angular.copy(_.get(card, fromPath));
        this.setValue(parent, toPath, value, card);
      });
    };

    const reset = (card, parent) => {
      let value = angular.copy(_.get(parent, toPath));
      _.set(card, fromPath, value);
    };

    return { action, reset };
  }

  setValue(target, path, value, card) {
    _.set(target, path, value);

    // Check for an error after setting value
    if (
      card.error &&
      card.error.checkOn &&
      card.error.checkOn.indexOf(path) > -1
    ) {
      card.error.check();
    }

    // Trigger onChange callback
    if (this.onChangeCallback) {
      this.onChangeCallback(card);
    }
  }

  sectionWithBinding(name, toBinding, fromBinding, options) {
    const binding = this.twoWayBinding(toBinding, fromBinding);
    this.sectionWithCallback(name, binding.action, binding.reset, options);
  }

  sectionWithCallback(name, callback, reset, options) {
    // Create section
    const section = this.createSection(name, callback, reset, options);

    // Add section to card
    if (!this.card.sections) {
      this.card.sections = [];
    }
    this.card.sections.push(section);
  }

  createSection(name, callback, reset, options) {
    return {
      name,
      action: this.wrapAction(callback),
      reset: this.wrapAction(reset),
      options,
    };
  }

  wrapAction(action) {
    return (...additionalArguments) => {
      const args = additionalArguments || [];
      action(this.card, this.parent, ...args);
    };
  }

  getCard() {
    return this.card;
  }
}
